import { useEffect, useState } from "react";
import { CreditCard, Loader2, Link2Off } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_STYLES = {
  whop: { bar: "bg-violet-500", chip: "text-violet-300 bg-violet-500/10 border-violet-500/30" },
  stripe: { bar: "bg-indigo-500", chip: "text-indigo-300 bg-indigo-500/10 border-indigo-500/30" },
  commas: { bar: "bg-emerald-500", chip: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
  paypal: { bar: "bg-sky-500", chip: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
};

function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

/**
 * Iter238 — "This week" cash collected per payment integration source
 * (Whop / Stripe / Commas / PayPal) for the Manager Dashboard.
 * Reads `/api/dashboard/payment-sources-widget`.
 */
export function PaymentSourcesWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/dashboard/payment-sources-widget`, { credentials: "include" });
        if (r.ok) setData(await r.json());
      } catch (e) {
        console.error("payment-sources-widget load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-center h-40" data-testid="payment-sources-widget-loading">
        <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
      </div>
    );
  }

  const sources = data?.sources || [];
  const maxCash = Math.max(1, ...sources.map((s) => s.cash_collected));

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="payment-sources-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-white">Payment Sources</h3>
        </div>
        <span className="text-xs text-gray-500" data-testid="payment-sources-week-label">
          {data?.week_label || "This week"}
        </span>
      </div>

      <div className="space-y-3">
        {sources.map((s) => {
          const style = SOURCE_STYLES[s.source] || SOURCE_STYLES.commas;
          const widthPct = s.connected ? Math.max(4, (s.cash_collected / maxCash) * 100) : 0;
          return (
            <div key={s.source} data-testid={`payment-source-row-${s.source}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-200">{s.label}</span>
                  {!s.connected && (
                    <span
                      className="flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-white/10 text-gray-500"
                      data-testid={`payment-source-not-connected-${s.source}`}
                    >
                      <Link2Off className="w-2.5 h-2.5" /> Not connected
                    </span>
                  )}
                </div>
                <span
                  className={`text-sm font-semibold ${s.connected ? "text-white" : "text-gray-600"}`}
                  data-testid={`payment-source-amount-${s.source}`}
                >
                  {s.connected ? fmtMoney(s.cash_collected) : "—"}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className={`h-full rounded-full ${style.bar} transition-all duration-500`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between">
        <span className="text-xs text-gray-500">Total cash collected</span>
        <span className="text-sm font-bold text-emerald-400" data-testid="payment-sources-total">
          {fmtMoney(data?.total_cash_collected)}
        </span>
      </div>
    </div>
  );
}
